// src/pages/Soporte.jsx — bandeja de chat en vivo con clientes
// El chatbot canaliza al cliente aquí cuando pide un asesor.
import React, { useCallback, useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { authFetch } from "../utils/authFetch";
import "./Soporte.css";
import {
  Boton, EncabezadoPagina, Iconos, Insignia,
} from "../componentes/base";

const REFRESCO_LISTA = 8000;   // ms
const REFRESCO_CHAT = 3000;    // ms

/* Qué color le toca a cada estado de una conversación. Va aquí y no
   dentro del render para que se lea de un vistazo qué estados existen. */
const VARIANTE_ESTADO = {
  pendiente: "suspendido",   // alguien espera respuesta
  atendida: "activo",
  cerrada: "cancelado",
};

export default function Soporte() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [conversaciones, setConversaciones] = useState([]);
  const [filtro, setFiltro] = useState("activas"); // activas | cerradas
  const [activa, setActiva] = useState(null);      // {conversacion, mensajes}
  const [texto, setTexto] = useState("");
  const [enviando, setEnviando] = useState(false);
  const finRef = useRef(null);
  const activaIdRef = useRef(null);

  // ---------- Cargar bandeja ----------
  const cargarLista = useCallback(async () => {
    try {
      const q = filtro === "cerradas" ? "?estado=cerrada" : "";
      const res = await authFetch(`/api/soporte/conversaciones${q}`);
      const data = await res.json();
      if (data.ok) setConversaciones(data.conversaciones || []);
    } catch (e) {
      console.log("Error cargando conversaciones:", e);
    }
  }, [filtro]);

  useEffect(() => {
    cargarLista();
    const t = setInterval(cargarLista, REFRESCO_LISTA);
    return () => clearInterval(t);
  }, [cargarLista]);

  // ---------- Abrir / refrescar conversación ----------
  const abrir = useCallback(async (id, scroll = true) => {
    try {
      const res = await authFetch(`/api/soporte/conversaciones/${id}/mensajes`);
      const data = await res.json();
      if (data.ok) {
        setActiva({ conversacion: data.conversacion, mensajes: data.mensajes || [] });
        activaIdRef.current = id;
        if (scroll) {
          setTimeout(() => finRef.current?.scrollIntoView({ behavior: "smooth" }), 60);
        }
      }
    } catch (e) {
      console.log("Error abriendo conversación:", e);
    }
  }, []);

  // Si llegamos desde el globo de aviso (/soporte?conv=12), abrirla
  useEffect(() => {
    const id = searchParams.get("conv");
    if (id) {
      abrir(Number(id));
      setSearchParams({}, { replace: true }); // limpia la URL
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  // Polling de la conversación abierta (para ver lo que escribe el cliente)
  useEffect(() => {
    if (!activa?.conversacion?.id) return;
    const id = activa.conversacion.id;
    const t = setInterval(() => {
      authFetch(`/api/soporte/conversaciones/${id}/mensajes`)
        .then((r) => r.json())
        .then((d) => {
          if (!d.ok) return;
          setActiva((prev) => {
            if (!prev || prev.conversacion.id !== id) return prev;
            const antes = prev.mensajes.length;
            const ahora = (d.mensajes || []).length;
            if (ahora > antes) {
              setTimeout(() => finRef.current?.scrollIntoView({ behavior: "smooth" }), 60);
            }
            return { conversacion: d.conversacion, mensajes: d.mensajes || [] };
          });
        })
        .catch(() => {});
    }, REFRESCO_CHAT);
    return () => clearInterval(t);
  }, [activa?.conversacion?.id]);

  // ---------- Enviar respuesta ----------
  async function enviar(e) {
    e?.preventDefault();
    const msg = texto.trim();
    if (!msg || !activa?.conversacion?.id || enviando) return;
    setEnviando(true);
    try {
      const res = await authFetch(
        `/api/soporte/conversaciones/${activa.conversacion.id}/mensajes`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ mensaje: msg }),
        }
      );
      const data = await res.json();
      if (data.ok) {
        setTexto("");
        await abrir(activa.conversacion.id);
        cargarLista();
      }
    } catch (e) {
      console.log("Error enviando mensaje:", e);
    } finally {
      setEnviando(false);
    }
  }

  async function cerrar() {
    if (!activa?.conversacion?.id) return;
    if (!window.confirm("¿Cerrar esta conversación?")) return;
    await authFetch(`/api/soporte/conversaciones/${activa.conversacion.id}/cerrar`, {
      method: "POST",
    });
    setActiva(null);
    cargarLista();
  }

  async function borrar(id) {
    if (!window.confirm("¿Borrar definitivamente esta conversación y sus mensajes?")) return;
    const res = await authFetch(`/api/soporte/conversaciones/${id}`, { method: "DELETE" });
    const data = await res.json();
    if (!data.ok) {
      alert(data.mensaje || "No se pudo borrar");
      return;
    }
    if (activa?.conversacion?.id === id) setActiva(null);
    cargarLista();
  }

  async function limpiarAntiguas() {
    const dias = window.prompt(
      "Borrar conversaciones CERRADAS con más de … días:", "90"
    );
    if (dias === null) return;
    const res = await authFetch("/api/soporte/conversaciones/limpiar", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ dias: Number(dias) || 90 }),
    });
    const data = await res.json();
    if (data.ok) {
      alert(`Se borraron ${data.borradas} conversación(es) de más de ${data.dias} días.`);
      cargarLista();
    } else {
      alert(data.mensaje || "No se pudo limpiar");
    }
  }

  function hora(v) {
    if (!v) return "";
    const d = new Date(v);
    return isNaN(d) ? "" : d.toLocaleString("es-MX", {
      day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit",
    });
  }

  const conv = activa?.conversacion;

  return (
    <div className="page-container sop-page">
      <EncabezadoPagina
        icono={Iconos.soporte}
        titulo="Soporte"
        resumen={`Chat con clientes · ${conversaciones.length} ${filtro}`}
      />

      <div className="sop-layout">
        {/* ================= BANDEJA ================= */}
        <aside className="sop-lista">
          <div className="sop-tabs">
            <button
              className={filtro === "activas" ? "activo" : ""}
              onClick={() => setFiltro("activas")}
            >
              Activas
            </button>
            <button
              className={filtro === "cerradas" ? "activo" : ""}
              onClick={() => setFiltro("cerradas")}
            >
              Cerradas
            </button>
          </div>

          {filtro === "cerradas" && conversaciones.length > 0 && (
            <Boton
              className="sop-limpiar"
              variante="secundario"
              icono={Iconos.eliminar}
              onClick={limpiarAntiguas}
            >
              Borrar cerradas antiguas…
            </Boton>
          )}

          {conversaciones.length === 0 ? (
            <p className="sop-vacio">
              {filtro === "activas"
                ? "Sin conversaciones activas."
                : "Sin conversaciones cerradas."}
            </p>
          ) : (
            conversaciones.map((c) => (
              <button
                key={c.id}
                className={
                  "sop-item" +
                  (conv?.id === c.id ? " sel" : "") +
                  (c.estado === "pendiente" ? " pendiente" : "")
                }
                onClick={() => abrir(c.id)}
              >
                <div className="sop-item-top">
                  <strong>{c.nombre || "Sin nombre"}</strong>
                  {c.sin_leer > 0 && <span className="sop-badge">{c.sin_leer}</span>}
                </div>
                <div className="sop-item-sub">
                  {c.telefono || "s/tel"} · {c.colonia || c.ciudad || ""}
                </div>
                <div className="sop-item-msg">{c.ultimo_mensaje || c.motivo}</div>
                <div className="sop-item-pie">
                  <Insignia variante={VARIANTE_ESTADO[c.estado] || "neutra"} texto={c.estado} />
                  <span>{hora(c.actualizado_en)}</span>
                </div>
              </button>
            ))
          )}
        </aside>

        {/* ================= CONVERSACIÓN ================= */}
        <section className="sop-chat">
          {!conv ? (
            <div className="sop-placeholder">
              <Iconos.soporte size={40} strokeWidth={1.4} />
              <p>Selecciona una conversación de la izquierda.</p>
            </div>
          ) : (
            <>
              <header className="sop-chat-head">
                <div>
                  <strong>{conv.nombre || "Sin nombre"}</strong>
                  <div className="sop-chat-sub">
                    {conv.telefono || "s/tel"}
                    {conv.correo ? ` · ${conv.correo}` : ""}
                    {conv.ciudad ? ` · ${conv.ciudad}` : ""}
                    {conv.colonia ? `, ${conv.colonia}` : ""}
                    {conv.tipo_servicio ? ` · ${conv.tipo_servicio}` : ""}
                  </div>
                </div>
                {conv.estado !== "cerrada" ? (
                  <Boton variante="secundario" icono={Iconos.confirmar} onClick={cerrar}>
                    Cerrar conversación
                  </Boton>
                ) : (
                  <Boton
                    variante="peligro-suave"
                    icono={Iconos.eliminar}
                    onClick={() => borrar(conv.id)}
                  >
                    Borrar
                  </Boton>
                )}
              </header>

              <div className="sop-mensajes">
                {activa.mensajes.map((m) => (
                  <div key={m.id} className={`sop-msg ${m.autor}`}>
                    <div className="sop-burbuja">
                      {m.autor === "bot" && <span className="sop-autor">Bot</span>}
                      {m.autor === "asesor" && <span className="sop-autor">Asesor</span>}
                      <div>{m.mensaje}</div>
                      <span className="sop-hora">{hora(m.creado_en)}</span>
                    </div>
                  </div>
                ))}
                <div ref={finRef} />
              </div>

              {conv.estado === "cerrada" ? (
                <div className="sop-cerrada">Esta conversación está cerrada.</div>
              ) : (
                <form className="sop-enviar" onSubmit={enviar}>
                  <input
                    value={texto}
                    onChange={(e) => setTexto(e.target.value)}
                    placeholder="Escribe tu respuesta al cliente…"
                    disabled={enviando}
                    autoFocus
                  />
                  <Boton
                    type="submit"
                    icono={Iconos.adelante}
                    cargando={enviando}
                    disabled={!texto.trim()}
                  >
                    Enviar
                  </Boton>
                </form>
              )}
            </>
          )}
        </section>
      </div>
    </div>
  );
}
